import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, FilterIcon, TagIcon } from 'lucide-react';
import { TagType } from './TagsManagement';
interface TagTypesListProps {
  globalTagTypes: TagType[];
  orgUnitTagTypes: TagType[];
  onAdd: () => void;
  onEdit: (tagType: TagType) => void;
  onDelete: (id: string) => void;
  locale: string;
}
export function TagTypesList({
  globalTagTypes,
  orgUnitTagTypes,
  onAdd,
  onEdit,
  onDelete,
  locale
}: TagTypesListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  // Apply search filter to a tag type list
  const filterTagTypes = (tagTypes: TagType[]) => {
    return tagTypes.filter(tagType => {
      return tagType.name.toLowerCase().includes(searchTerm.toLowerCase()) || tagType.description && tagType.description.toLowerCase().includes(searchTerm.toLowerCase());
    });
  };
  const filteredGlobalTagTypes = filterTagTypes(globalTagTypes);
  const filteredOrgUnitTagTypes = filterTagTypes(orgUnitTagTypes);
  // Render color palette
  const renderColorPalette = (colors: string[] | null) => {
    if (!colors || colors.length === 0) {
      return <span className="text-gray-400 italic" data-id="element-20668">No color palette defined</span>;
    }
    return <div className="flex space-x-1" data-id="element-20669">
        {colors.map((color, index) => <div key={index} className="h-4 w-4 rounded-full" style={{
        backgroundColor: color
      }} title={color} data-id="element-20670" />)}
      </div>;
  };
  // Render tag type card
  const renderTagTypeCard = (tagType: TagType, showOrgUnit: boolean = false) => {
    return <div className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200" data-id="element-20671">
        <div className="px-4 py-5 sm:px-6" data-id="element-20672">
          <div className="flex justify-between items-start" data-id="element-20673">
            <div className="flex-1" data-id="element-20674">
              {showOrgUnit && tagType.orgUnitName && <div className="mb-2" data-id="element-20675">
                  <p className="text-xs font-medium text-gray-500" data-id="element-20676">
                    {tagType.orgUnitName}
                  </p>
                  <p className="text-xs text-gray-400" data-id="element-20677">{tagType.orgUnitPath}</p>
                </div>}
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-20678">
                <TagIcon className="h-5 w-5 mr-2 text-indigo-600" data-id="element-20679" />
                {tagType.name}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-20680">
                {tagType.description || <span className="text-gray-400 italic" data-id="element-20681">No description</span>}
              </p>
            </div>
            <div className="flex space-x-2 ml-4" data-id="element-20682">
              <button onClick={() => onEdit(tagType)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-20683">
                <EditIcon className="h-4 w-4" data-id="element-20684" />
              </button>
              <button onClick={() => onDelete(tagType.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-20685">
                <TrashIcon className="h-4 w-4" data-id="element-20686" />
              </button>
            </div>
          </div>
        </div>
        <div className="px-4 py-4 sm:px-6" data-id="element-20687">
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-20688">
            <div data-id="element-20689">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20690">
                Restricted
              </span>
              <p className="mt-1 text-sm text-gray-900" data-id="element-20691">
                {tagType.isRestricted ? 'Yes' : 'No'}
              </p>
              <p className="mt-1 text-xs text-gray-500" data-id="element-20692">
                {tagType.isRestricted ? 'Only predefined tag values are allowed' : 'Users can create any tag value'}
              </p>
            </div>
            <div data-id="element-20693">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20694">
                Color Palette
              </span>
              <div className="mt-1" data-id="element-20695">
                {renderColorPalette(tagType.colourPalette)}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-20696">
      {/* Search Bar */}
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-20697">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-20698">
          <div className="flex-1 max-w-md" data-id="element-20699">
            <div className="relative rounded-md shadow-sm" data-id="element-20700">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-20701">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-20702" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search tag types..." data-id="element-20703" />
            </div>
          </div>
          <button onClick={onAdd} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20704">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-20705" />
            Add Tag Type
          </button>
        </div>
      </div>

      {/* Global Tag Types */}
      <div data-id="element-20706">
        <div className="mb-4" data-id="element-20707">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20708">
            Global Tag Types
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20709">
            Global tag types available to all organisation units
          </p>
        </div>
        {filteredGlobalTagTypes.length === 0 ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-20710">
            <div className="px-4 py-12 text-center" data-id="element-20711">
              <p className="text-sm text-gray-500" data-id="element-20712">
                No global tag types found.
              </p>
            </div>
          </div> : <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-20713">
            {filteredGlobalTagTypes.map(tagType => <div key={tagType.id} data-id="element-20714">{renderTagTypeCard(tagType, false)}</div>)}
          </div>}
      </div>

      {/* OrgUnit Tag Types */}
      <div data-id="element-20715">
        <div className="mb-4" data-id="element-20716">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20717">
            Organisation Unit Tag Types
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20718">
            Tag types specific to individual organisation units
          </p>
        </div>
        {filteredOrgUnitTagTypes.length === 0 ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-20719">
            <div className="px-4 py-12 text-center" data-id="element-20720">
              <p className="text-sm text-gray-500" data-id="element-20721">
                No organisation unit tag types found.
              </p>
            </div>
          </div> : <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-20722">
            {filteredOrgUnitTagTypes.map(tagType => <div key={tagType.id} data-id="element-20723">{renderTagTypeCard(tagType, true)}</div>)}
          </div>}
      </div>
    </div>;
}